import {
  TREATMENT_RUNTIME_STATE,
} from "../treatmentApiConstants";
import { authorityStateForQuestion } from "../authorityProgress";
import { AUTH_BIND_DISPOSITION } from "./authenticationConstants";

const profileIsIncomplete = (profile) => (
  Array.isArray(profile?.missing_fields) && profile.missing_fields.length > 0
);

const bindingOutcome = (disposition, profile, projection) => ({
  disposition,
  profile,
  projection,
});

/**
 * Find the first routing question after the authentication boundary.
 *
 * Authentication has no separate runtime state, so authorityStateForQuestion
 * intentionally falls back to routing for it. The boundary itself must still
 * be excluded here or a completed login/registration would target the same
 * item again; the authentication component renders null once it is complete.
 *
 * Custom Programs can place authentication after some Matching Questions
 * (WT-123), so only questions after the boundary are candidates.
 */
export const firstPostAuthenticationRoutingQuestion = (questions) => {
  const ordered = Array.isArray(questions) ? questions : [];
  const authenticationIndex = ordered.findIndex(
    (question) => question?.question_type === "patient_authentication",
  );
  return ordered.slice(authenticationIndex + 1).find((question) => (
    (
      question?.question_type === "state_routing"
      || question?.kind === "state_routing"
      || question?.source_id === "service_state"
    )
    && authorityStateForQuestion(question) === TREATMENT_RUNTIME_STATE.routing
  )) || null;
};

/**
 * Finish the runtime authority handoff before authentication advances locally.
 *
 * A live bind intentionally leaves an eligible standalone Program in routing.
 * When the next visible question crosses out of routing, that boundary must be
 * submitted before the server-authoritative screening step can render.
 */
export const completePostBindAuthority = async ({
  bound,
  expectedState = null,
  fallbackProfile = null,
  patient = null,
  synchronizeNextJourneyStep,
}) => {
  const profile = bound?.profile || fallbackProfile || null;

  if (bound?.state === TREATMENT_RUNTIME_STATE.disqualified) {
    return bindingOutcome(
      AUTH_BIND_DISPOSITION.disqualified,
      profile,
      bound,
    );
  }
  if (profileIsIncomplete(profile)) {
    return bindingOutcome(
      AUTH_BIND_DISPOSITION.profileIncomplete,
      profile,
      bound,
    );
  }

  let projection = bound;
  if (bound?.state === TREATMENT_RUNTIME_STATE.routing) {
    // The runtime projection is authoritative about whether routing still
    // needs patient input. Custom-program releases can place a matching
    // question before the routing node in questionnaire JSON, so the next
    // visible UI question is not sufficient to decide whether it is safe to
    // submit an answer_routing transition here.
    const authoredRoutingQuestion = Array.isArray(bound?.step?.questions)
      && bound.step.questions.some((question) => (
        question?.question_type === "state_routing"
        || question?.kind === "state_routing"
        || question?.source_id === "service_state"
      ));
    if (!expectedState) {
      throw new Error("Treatment session could not advance after authentication.");
    }
    if (
      expectedState === TREATMENT_RUNTIME_STATE.routing
      || authoredRoutingQuestion
    ) {
      return bindingOutcome(
        AUTH_BIND_DISPOSITION.ready,
        profile,
        bound,
      );
    }
    if (typeof synchronizeNextJourneyStep !== "function") {
      throw new Error("Treatment session could not advance after authentication.");
    }
    projection = await synchronizeNextJourneyStep(patient);
    if (!projection || projection.state !== expectedState) {
      if (projection?.state === TREATMENT_RUNTIME_STATE.disqualified) {
        const resolvedProfile = projection.profile || profile;
        return bindingOutcome(
          AUTH_BIND_DISPOSITION.disqualified,
          resolvedProfile,
          projection,
        );
      }
      throw new Error("Treatment session could not advance after authentication.");
    }
  }

  const resolvedProfile = projection?.profile || profile;
  if (projection?.state === TREATMENT_RUNTIME_STATE.disqualified) {
    return bindingOutcome(
      AUTH_BIND_DISPOSITION.disqualified,
      resolvedProfile,
      projection,
    );
  }
  if (profileIsIncomplete(resolvedProfile)) {
    return bindingOutcome(
      AUTH_BIND_DISPOSITION.profileIncomplete,
      resolvedProfile,
      projection,
    );
  }
  return bindingOutcome(
    AUTH_BIND_DISPOSITION.ready,
    resolvedProfile,
    projection,
  );
};

/** Claim the one local move out of the authentication step. */
export const claimAuthenticationAdvance = (advanceRef) => {
  if (!advanceRef || advanceRef.current) return false;
  advanceRef.current = true;
  return true;
};

/** Reset the duplicate guard only when a new authentication-step visit begins. */
export const trackAuthenticationStepVisit = ({
  advanceRef,
  previousStepIdRef,
  stepId,
  isAuthentication,
}) => {
  if (!advanceRef || !previousStepIdRef) return;
  const normalizedStepId = String(stepId || "");
  if (
    isAuthentication
    && previousStepIdRef.current !== normalizedStepId
  ) {
    advanceRef.current = false;
  }
  previousStepIdRef.current = normalizedStepId;
};
